import { HostAdventure } from "./Adventures.js";
import { HostAppeals, HostRewards } from "./Rewards.js";
import { useEffect, useState } from "react";
import type { Difficulty, DmAction, DmTurnSummary, HostPanelResponse, Member, VoiceMode } from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";
import { HpBar, HpControls } from "./Hp.js";

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const day = (iso: string) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const DIFFICULTIES: Array<{ id: Difficulty; label: string; hint: string }> = [
  { id: "easy", label: "Easy", hint: "Lower DCs, fewer and less tactical foes, generous hints" },
  { id: "normal", label: "Normal", hint: "Standard 5e" },
  { id: "hard", label: "Hard", hint: "Tougher DCs, smarter foes, scarcer resources" },
];

function actionLabel(a: DmAction): string {
  switch (a.type) {
    case "apply_damage": return `Damage ${a.target} ${a.amount}`;
    case "heal": return `Heal ${a.target} ${a.amount}`;
    case "set_temp_hp": return `Temp HP ${a.target} ${a.amount}`;
    case "add_monster": return `Add ${a.count && a.count > 1 ? `${a.count}× ` : ""}${a.name}`;
    case "request_roll": return `Ask ${a.target} to roll ${a.check ?? a.formula ?? a.label ?? ""}`;
    case "roll_dice": return `Roll ${a.formula}`;
    case "set_condition": return `${a.active ? "Set" : "Clear"} ${a.condition} on ${a.target}`;
    case "spend_spell_slot": return `${a.target} spends a level ${a.level} slot`;
    case "remember": return `Remember ${a.kind} "${a.name}"`;
    case "whisper": return `Whisper to ${a.target}`;
    default: return a.type.replace(/_/g, " ");
  }
}

/**
 * Host panel (Host, Co-hosts, Admin): pause/resume the DM, difficulty, rewind the DM's last reply, rule over it
 * (override or veto: final), quick HP for the party.
 */
export function HostPage() {
  const { campaign, fail, hostVersion, session, party } = useGame();
  const [data, setData] = useState<HostPanelResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [ruling, setRuling] = useState("");
  const load = () => api.hostPanel(campaign.id).then(setData, fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id, hostVersion, session?.paused]);
  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    try {
      const msg = await fn();
      if (msg) setNote(msg);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  if (!data) return <main className="muted">Loading…</main>;
  const latest = data.turns.find((t) => !t.rewoundAt) ?? null;
  const paused = session?.paused ?? data.paused;

  return (
    <main className="host-page">
      {note && <div className="ok-box" onClick={() => setNote(null)}>{note}</div>}
      <section className="panel host-controls">
        <div className="host-row">
          <div>
            <h3>DM</h3>
            <p className="muted small">{paused ? "Paused: the DM listens but won't speak or change the game." : "Running."}</p>
          </div>
          <button
            className={paused ? "primary big" : "big"}
            disabled={busy}
            onClick={() => run(async () => { await api.setPaused(campaign.id, !paused); return paused ? "DM resumed." : "DM paused."; })}
          >
            {paused ? "▶ Resume DM" : "⏸ Pause DM"}
          </button>
        </div>
        <div className="host-row">
          <div>
            <h3>Difficulty</h3>
            <p className="muted small">{DIFFICULTIES.find((d) => d.id === data.difficulty)?.hint}</p>
          </div>
          <div className="segmented" role="radiogroup" aria-label="Difficulty">
            {DIFFICULTIES.map((d) => (
              <button
                key={d.id}
                role="radio"
                aria-checked={data.difficulty === d.id}
                className={data.difficulty === d.id ? "on" : ""}
                disabled={busy}
                title={d.hint}
                onClick={() => run(async () => { await api.setDifficulty(campaign.id, d.id); })}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      <PlayerVoices />
      <HostAdventure />
      <HostAppeals />
      <HostRewards />

      <section className="panel">
        <h3>Ruling</h3>
        <p className="muted small">Your call is final: the DM follows it and won't argue. Veto also rewinds the DM's last reply.</p>
        <div className="form-row">
          <input className="grow" value={ruling} maxLength={1000} placeholder='e.g. "The rope bridge holds." or "No, the guard didn&apos;t see you."' onChange={(e) => setRuling(e.target.value)} />
          <button
            className="primary"
            disabled={busy || !ruling.trim()}
            onClick={() => run(async () => { await api.ruling(campaign.id, ruling.trim(), false); setRuling(""); return "Ruling sent to the DM."; })}
          >
            Override
          </button>
          <button
            className="danger"
            disabled={busy || !latest}
            title={latest ? "Rewind the DM's last reply and send this ruling" : "Nothing to veto yet"}
            onClick={() =>
              run(async () => {
                const r = await api.ruling(campaign.id, ruling.trim(), true);
                setRuling("");
                return r.rewind ? `Vetoed: rewound the DM's last reply${r.rewind.skipped ? ` (${r.rewind.skipped} change(s) kept because they were edited since)` : ""}.` : "Veto sent.";
              })
            }
          >
            Veto last reply
          </button>
        </div>
        <details className="planner">
          <summary>DM's background thinking (private: never spoken or shown to players)</summary>
          {data.planner ? (
            <>
              <p className="muted small">Updated {new Date(data.planner.updatedAt).toLocaleTimeString()}</p>
              <pre style={{ whiteSpace: "pre-wrap" }}>{data.planner.text}</pre>
            </>
          ) : (
            <p className="muted small">No planning note yet. It appears a few seconds after the DM's first reply when "Background thinking" is on.</p>
          )}
        </details>
        {data.rulings.length > 0 && (
          <ul className="rulings">
            {data.rulings.slice(0, 8).map((r) => (
              <li key={r.id}>
                <span className={`tag ${r.kind === "veto" ? "veto" : ""}`}>{r.kind === "veto" ? "Veto" : "Ruling"}</span> {r.text}{" "}
                <span className="muted small">{r.createdByName ? `${r.createdByName}, ` : ""}{time(r.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <h3>Rewind</h3>
        <p className="muted small">
          Undo the DM's last reply: it's struck from the transcript, the DM forgets it, and its game changes (HP, conditions,
          monsters, combat, roll requests, memory notes) are reverted. Dice rolls stay in the log. Rewind again to go back further.
        </p>
        {data.turns.length === 0 && <p className="muted">The DM hasn't said anything yet.</p>}
        {data.turns.map((t) => (
          <Turn
            key={t.id}
            t={t}
            canRewind={t.id === latest?.id}
            busy={busy}
            onRewind={() =>
              run(async () => {
                if (!window.confirm("Rewind the DM's last reply?")) return;
                const r = await api.rewind(campaign.id, t.id);
                return `Rewound: ${r.restored} change(s) undone${r.skipped ? `, ${r.skipped} kept because someone changed them since` : ""}.`;
              })
            }
          />
        ))}
      </section>

      <section className="panel">
        <h3>Quick HP</h3>
        {party.length === 0 && <p className="muted">No characters yet.</p>}
        <ul className="quick-hp">
          {party.map((c) => (
            <li key={c.id}>
              <span>
                <strong>{c.name}</strong> <span className="muted small">{c.ownerName}</span>
              </span>
              <HpBar hp={c.currentHp} maxHp={c.maxHp} tempHp={c.tempHp} />
              <HpControls onChange={async (kind, amount) => { await api.characterHp(campaign.id, c.id, kind, amount).catch(fail); }} />
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function Turn({ t, canRewind, busy, onRewind }: { t: DmTurnSummary; canRewind: boolean; busy: boolean; onRewind: () => void }) {
  return (
    <div className={`turn ${t.rewoundAt ? "rewound" : ""}`}>
      <div className="row-between">
        <span className="muted small">
          {time(t.createdAt)}
          {t.speakerName ? ` · answering ${t.speakerName}` : ""}
          {t.rewoundAt ? ` · rewound${t.rewoundByName ? ` by ${t.rewoundByName}` : ""}` : ""}
        </span>
        {canRewind && (
          <button className="danger small" disabled={busy} onClick={onRewind}>
            ⟲ Rewind
          </button>
        )}
      </div>
      {t.lines.length ? t.lines.map((l, i) => <p key={i} className="turn-line">{l}</p>) : <p className="muted small">(no speech yet)</p>}
      {t.actions.length > 0 && (
        <ul className="turn-actions">
          {t.actions.map((a, i) => (
            <li key={i} className={a.reverted ? "reverted" : a.result.ok ? "" : "failed"}>
              {actionLabel(a.action)} <span className="muted small">→ {a.result.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const VOICE_MODE_LABELS: Array<{ id: VoiceMode; label: string; hint: string }> = [
  { id: "normal", label: "Normal", hint: "The DM hears this player's voice as usual" },
  { id: "hold", label: "Hold to talk", hint: "Only speech while they hold the Talk button on the Table page counts (for open or noisy mics)" },
  { id: "typed", label: "Typed only", hint: "Their voice is ignored; they type to the DM on the Table page" },
  { id: "ignored", label: "Ignored", hint: "The DM ignores this player's voice and typing" },
];

/** Host tab: how the DM treats each player's voice, plus open-mic / audio-trouble notices. */
function PlayerVoices() {
  const { campaign, session, setSession, fail } = useGame();
  const [members, setMembers] = useState<Member[]>([]);
  useEffect(() => {
    api.campaignDetail(campaign.id).then((d) => setMembers(d.members), fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);
  const modes = session?.voiceModes ?? {};
  const inVoice = new Set(session?.voiceMemberIds ?? []);
  const set = (userId: string, mode: VoiceMode) => api.setVoiceMode(campaign.id, userId, mode).then(setSession, fail);
  if (!members.length) return null;
  return (
    <section className="panel">
      <h3>Players' voices</h3>
      <p className="muted small">
        For an open or noisy mic: <strong>Hold to talk</strong> makes the DM listen to that player only while they hold the Talk button on the
        Table page; <strong>Typed only</strong> ignores their voice. When the DM hears a lot of background noise from someone, a notice appears at the top of the page for you and that player.
      </p>
      <div className="voice-modes">
        {members.map((m) => {
          const mode = modes[m.userId] ?? "normal";
          return (
            <div key={m.userId} className="host-row">
              <div>
                <strong>{m.displayName}</strong>
                {m.characterName ? <span className="muted"> ({m.characterName})</span> : null}
                {inVoice.has(m.userId) ? <span className="muted small"> · in voice</span> : null}
              </div>
              <div className="segmented" role="radiogroup" aria-label={`Voice mode for ${m.displayName}`}>
                {VOICE_MODE_LABELS.map((o) => (
                  <button key={o.id} role="radio" aria-checked={mode === o.id} className={mode === o.id ? "on" : ""} title={o.hint}
                    onClick={() => void set(m.userId, o.id)}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
